import { CPopover } from "@coreui/react";
import {
  REMISSION_NOTE_STATUS_APPROVED_BY_SET,
  REMISSION_NOTE_STATUS_CANCELLATION_FAILED,
  REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED,
  REMISSION_NOTE_STATUS_CANCELLED,
  REMISSION_NOTE_STATUS_CHOICES,
  REMISSION_NOTE_STATUS_ERROR,
  REMISSION_NOTE_STATUS_RESEND_REQUESTED,
  REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR,
  REMISSION_NOTE_STATUS_UPLOADED_TO_SET,
  RemissionNote,
} from "../../models/remission-note";
import { useEffect, useState } from "react";
import { useSub } from "../../hooks/pub-sub";
import DocumentError from "../../models/document-error";
import { getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";

interface RemissionNoteCrudStatusProps {
  remissionNote: RemissionNote;
}

const RemissionNoteCrudStatus: React.FC<RemissionNoteCrudStatusProps> = ({
  remissionNote,
}) => {
  const [currentRemissionNote, setCurrentRemissionNote] =
    useState(remissionNote);
  const [retrievedError, setRetrievedError] = useState<DocumentError>();
  const [loadingError, setLoadingError] = useState(false);

  useSub(
    `remission_note_${remissionNote.id}`,
    (remissionNote: RemissionNote) => {
      setCurrentRemissionNote(remissionNote);
    }
  );

  useEffect(() => {
    setCurrentRemissionNote(remissionNote);
  }, [remissionNote]);

  useEffect(() => {
    const fetchError = async () => {
      const errorCode = currentRemissionNote.setResponseCode;
      const errorMessage =
        currentRemissionNote.setResponseDescription ||
        currentRemissionNote.setError;

      if (
        !(
          currentRemissionNote.status === REMISSION_NOTE_STATUS_ERROR ||
          currentRemissionNote.status ===
            REMISSION_NOTE_STATUS_CANCELLATION_FAILED ||
          currentRemissionNote.status ===
            REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR
        )
      ) {
        setRetrievedError(undefined);
        return;
      }

      if (!errorCode && !errorMessage) {
        setRetrievedError(undefined);
        return;
      }

      setLoadingError(true);
      try {
        let result;
        if (errorCode) {
          const params = new Map<string, string | undefined>();
          params.set("error_code", errorCode);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params
          );
        }

        if (
          (!result || result.status !== SUCCESS || !result.data) &&
          errorMessage
        ) {
          const params = new Map<string, string | undefined>();
          params.set("raw_message", errorMessage);
          result = await getItem<DocumentError>(
            "/document_errors/search",
            params
          );
        }

        if (result?.status === SUCCESS && result.data) {
          setRetrievedError(result.data);
        } else {
          setRetrievedError(undefined);
        }
      } catch (err) {
        console.error("Error loading document error:", err);
        setRetrievedError(undefined);
      } finally {
        setLoadingError(false);
      }
    };

    fetchError();
  }, [currentRemissionNote]);

  let badgeClassName = "badge-info";

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_UPLOADED_TO_SET) {
    badgeClassName = "badge-warning";
  }

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR) {
    badgeClassName = "badge-danger";
  }

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_APPROVED_BY_SET) {
    badgeClassName = "badge-success";
  }

  if (
    currentRemissionNote.status === REMISSION_NOTE_STATUS_CANCELLATION_REQUESTED
  ) {
    badgeClassName = "badge-light";
  }

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_CANCELLED) {
    badgeClassName = "badge-info";
  }

  if (
    currentRemissionNote.status === REMISSION_NOTE_STATUS_CANCELLATION_FAILED
  ) {
    badgeClassName = "badge-danger";
  }

  if (currentRemissionNote.status === REMISSION_NOTE_STATUS_RESEND_REQUESTED) {
    badgeClassName = "badge-warning";
  }

  const renderPopoverContent = () => {
    if (
      ![
        REMISSION_NOTE_STATUS_ERROR,
        REMISSION_NOTE_STATUS_CANCELLATION_FAILED,
        REMISSION_NOTE_STATUS_SET_UNKNOWN_ERROR,
      ].includes(currentRemissionNote.status!)
    )
      return null;

    return (
      <ul
        style={{
          paddingLeft: "10px",
          paddingRight: "10px",
          paddingBottom: "0px",
          marginBottom: "0px",
        }}
      >
        {currentRemissionNote.setResponseCode && (
          <li>Código: {currentRemissionNote.setResponseCode}</li>
        )}
        {currentRemissionNote.setResponseDescription && (
          <li>Descripción: {currentRemissionNote.setResponseDescription}</li>
        )}
        {currentRemissionNote.setError && (
          <li>Error: {currentRemissionNote.setError}</li>
        )}
        {!loadingError && retrievedError && (
          <>
            {retrievedError.description && (
              <li>Significado: {retrievedError.description}</li>
            )}
            {retrievedError.possibleSolutions && (
              <li>Posible solución: {retrievedError.possibleSolutions}</li>
            )}
          </>
        )}
      </ul>
    );
  };

  return (
    <td className="text-center">
      <span
        className={`badge badge-pill ${badgeClassName}`}
        style={{ fontSize: "14px" }}
      >
        {REMISSION_NOTE_STATUS_CHOICES.get(currentRemissionNote.status!)}{" "}
        {renderPopoverContent() ? (
          <CPopover
            content={renderPopoverContent()}
            placement="top"
            trigger="click"
          >
            <span
              className="fa fa-info-circle"
              style={{ cursor: "pointer" }}
            ></span>
          </CPopover>
        ) : null}
      </span>
    </td>
  );
};

export default RemissionNoteCrudStatus;
